// Shared cscoach building blocks on top of shadcn/ui.
import type { ReactNode } from "react";
import { Bot, ChevronDown, Crosshair, Loader2, Sparkles } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { mapBackground, mapIcon } from "@/lib/map-icons";
import { mapTheme } from "@/lib/maps";
import { mapName } from "@/lib/format";
import { cn } from "@/lib/utils";

export function Logo({ className }: { className?: string }) {
  return (
    <span className={cn("flex items-center gap-2 font-display text-lg font-bold tracking-wider", className)}>
      <span className="grid size-7 place-items-center rounded-md bg-primary text-primary-foreground shadow-[0_0_18px_-4px_var(--primary)]">
        <Crosshair className="size-4" strokeWidth={2.5} />
      </span>
      <span>
        CS<span className="text-primary">COACH</span>
      </span>
    </span>
  );
}

export function PageTitle({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="truncate text-2xl font-bold sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

/** A big number with a label; `hint` shows on hover/tap. */
export function StatTile({ value, label, hint, tone }: { value: ReactNode; label: string; hint?: string; tone?: "good" | "bad" }) {
  const tile = (
    <Card className="gap-0 py-0">
      <CardContent className="px-4 py-3">
        <div className={cn("num font-display text-2xl font-bold", tone === "good" && "text-win", tone === "bad" && "text-loss")}>{value}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
  if (!hint) return tile;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>{tile}</div>
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

const STATUS: Record<string, { label: string; className: string }> = {
  uploaded: { label: "в очереди", className: "border-primary/40 text-primary" },
  queued: { label: "в очереди", className: "border-primary/40 text-primary" },
  parsing: { label: "разбор", className: "border-primary/40 text-primary animate-pulse" },
  recording: { label: "запись", className: "border-primary/40 text-primary animate-pulse" },
  running: { label: "пишется", className: "border-ai-to/40 text-ai-to animate-pulse" },
  leased: { label: "выполняется", className: "border-primary/40 text-primary animate-pulse" },
  parsed: { label: "готово", className: "border-win/40 text-win" },
  ready: { label: "готово", className: "border-win/40 text-win" },
  done: { label: "готово", className: "border-win/40 text-win" },
  duplicate: { label: "уже есть", className: "border-win/40 text-win" },
  failed: { label: "ошибка", className: "border-loss/40 text-loss" },
  canceled: { label: "отменено", className: "text-muted-foreground" },
  deleted: { label: "удалено", className: "text-muted-foreground" },
  expired: { label: "истекло", className: "text-muted-foreground" },
};

export function StatusBadge({ status }: { status: string }) {
  const s = STATUS[status] ?? { label: status, className: "" };
  return (
    <Badge variant="outline" className={cn("whitespace-nowrap", s.className)}>
      {s.label}
    </Badge>
  );
}

export function ResultBadge({ result }: { result: "win" | "loss" | "draw" }) {
  const map = { win: ["Победа", "bg-win/15 text-win"], loss: ["Поражение", "bg-loss/15 text-loss"], draw: ["Ничья", "bg-muted text-muted-foreground"] } as const;
  return <Badge className={cn("border-0", map[result][1])}>{map[result][0]}</Badge>;
}

export function TeamScore({ a, b, nameA, nameB, winner, size = "lg" }: { a: number; b: number; nameA: string; nameB: string; winner: "A" | "B" | null; size?: "lg" | "sm" }) {
  return (
    // Names wrap to two lines instead of being cut: on a phone "Команда A" and
    // "Команда B" both became "Команд…".
    <div className={cn("flex items-center gap-3", size === "lg" ? "text-xl sm:text-4xl" : "text-base")}>
      <span className={cn("line-clamp-2 min-w-0 flex-1 break-words text-right font-display font-bold leading-tight text-ct", winner === "B" && "opacity-70")} title={nameA}>{nameA}</span>
      <span className="num shrink-0 font-display font-bold">
        <span className={winner === "A" ? "text-foreground glow-primary" : "text-muted-foreground"}>{a}</span>
        <span className="mx-1.5 text-muted-foreground">:</span>
        <span className={winner === "B" ? "text-foreground glow-primary" : "text-muted-foreground"}>{b}</span>
      </span>
      <span className={cn("line-clamp-2 min-w-0 flex-1 break-words font-display font-bold leading-tight text-t", winner === "A" && "opacity-70")} title={nameB}>{nameB}</span>
    </div>
  );
}

/** Avatar (animated GIF when the player has one) with an optional Steam avatar frame on top. */
export function PlayerAvatar({ name, src, frame, className }: { name: string; src?: string | null; frame?: string | null; className?: string }) {
  const avatar = (
    <Avatar className={cn("size-8 rounded-md", className)}>
      {src && <AvatarImage src={src} alt="" />}
      <AvatarFallback className="rounded-md bg-secondary font-display text-xs">{name.slice(0, 2).toUpperCase()}</AvatarFallback>
    </Avatar>
  );
  if (!frame) return avatar;
  // Steam draws frames about 22% larger than the avatar, centered.
  return (
    <span className="relative inline-block shrink-0">
      {avatar}
      <img src={frame} alt="" className="pointer-events-none absolute left-1/2 top-1/2 size-[122%] max-w-none -translate-x-1/2 -translate-y-1/2" />
    </span>
  );
}

/** Marks content written by the AI. */
export function AIBadge({ className }: { className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ai-frame", className)}>
      <Sparkles className="size-3 text-ai-to" />
      <span className="text-ai">ИИ-разбор</span>
    </span>
  );
}

export function AICard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("ai-frame relative overflow-hidden rounded-xl p-4 sm:p-5", className)}>
      <div className="pointer-events-none absolute -top-24 right-0 size-56 rounded-full bg-ai-from/15 blur-3xl" />
      <div className="relative">{children}</div>
    </div>
  );
}

export function EmptyState({ icon: Icon = Bot, title, children }: { icon?: typeof Bot; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-6 py-10 text-center">
      <Icon className="size-8 text-muted-foreground" />
      <div className="font-display font-semibold">{title}</div>
      {children && <div className="max-w-md text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

export function PageSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-9 w-2/3 max-w-sm" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
      <Skeleton className="h-64" />
    </div>
  );
}

export function ErrorState({ error }: { error: unknown }) {
  return <EmptyState title="Не получилось загрузить">{error instanceof Error ? error.message : String(error)}</EmptyState>;
}

/**
 * A map's banner: the in-game screenshot darkened towards the bottom, or,
 * for maps without one, a color gradient with the map name as a watermark.
 * Children sit on top (use .glass panels for text).
 */
export function MapBanner({ map, className, children, watermark = true }: { map: string | null | undefined; className?: string; children?: ReactNode; watermark?: boolean }) {
  const photo = mapBackground(map);
  const t = mapTheme(map);
  return (
    <div
      className={cn("relative isolate overflow-hidden", className)}
      style={photo ? undefined : { background: `radial-gradient(120% 90% at 85% 0%, ${t.from}, ${t.to} 70%)` }}
    >
      {photo ? (
        <>
          <img src={photo} alt="" loading="lazy" decoding="async" className="pointer-events-none absolute inset-0 -z-10 size-full object-cover" />
          <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-t from-card via-card/65 to-black/45" />
        </>
      ) : (
        <>
          <div className="hatch pointer-events-none absolute inset-0 -z-10" />
          <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-t from-card via-card/40 to-transparent" />
          {watermark && (
            <span
              className="pointer-events-none absolute -bottom-3 right-2 -z-10 select-none font-display text-6xl font-extrabold uppercase leading-none tracking-tight opacity-20 sm:text-8xl"
              style={{ color: t.accent }}
            >
              {mapName(map)}
            </span>
          )}
        </>
      )}
      {children}
    </div>
  );
}

/** The map's emblem (CS2 icon), or a colored square with its first letters. */
export function MapChip({ map, className }: { map: string | null | undefined; className?: string }) {
  const icon = mapIcon(map);
  if (icon) {
    return (
      <img src={icon} alt={mapName(map)} title={mapName(map)} loading="lazy" decoding="async" width={44} height={44}
        className={cn("size-11 shrink-0 object-contain drop-shadow-[0_2px_6px_rgb(0_0_0/0.45)]", className)} />
    );
  }
  const t = mapTheme(map);
  return (
    <span
      className={cn("grid size-11 shrink-0 place-items-center rounded-lg font-display text-xs font-bold uppercase", className)}
      style={{ background: `linear-gradient(135deg, ${t.from}, ${t.to})`, color: t.accent }}
    >
      {mapName(map).slice(0, 3)}
    </span>
  );
}

/** Win/loss squares of recent matches, oldest first. */
export function FormStrip({ results, className }: { results: ("win" | "loss" | "draw")[]; className?: string }) {
  return (
    <span className={cn("inline-flex gap-1", className)}>
      {results.map((r, i) => (
        <span
          key={i}
          title={r === "win" ? "победа" : r === "loss" ? "поражение" : "ничья"}
          className={cn("grid size-5 place-items-center rounded font-display text-[10px] font-bold",
            r === "win" ? "bg-win/20 text-win" : r === "loss" ? "bg-loss/20 text-loss" : "bg-muted text-muted-foreground")}
        >
          {r === "win" ? "В" : r === "loss" ? "П" : "Н"}
        </span>
      ))}
    </span>
  );
}

/** "Load more" under a paged list, with how much of it is shown. */
export function LoadMore({ onClick, loading, shown, total }: { onClick: () => void; loading: boolean; shown: number; total?: number }) {
  return (
    <div className="flex flex-col items-center gap-1.5 pt-2">
      <Button variant="outline" className="w-full sm:w-auto sm:min-w-56" onClick={onClick} disabled={loading}>
        {loading ? <Loader2 className="size-4 animate-spin" /> : <ChevronDown className="size-4" />}
        Загрузить ещё
      </Button>
      {total ? <span className="num text-xs text-muted-foreground">{shown} из {total}</span> : null}
    </div>
  );
}
